import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  Checkbox,
  DatePicker,
  FormField,
  Input,
  Modal,
  RadioGroup,
  Select,
  SpaceBetween,
  TextContent,
  Textarea,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationUpdateRequest,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  BulkOutcome,
  useBulkInvestigationActions,
} from "../../hooks/useInvestigations";
import { ToastsApi } from "../common/useToasts";
import {
  InvestigationMergeDialog,
  mergeBlocker,
} from "./InvestigationMergeAction";
import {
  AssigneeAutosuggest,
  normaliseAlias,
  useAssigneeDirectory,
} from "./AssigneeAutosuggest";
import { BlockerReasonField } from "./BlockerReasonField";
import { normalizeBlockerLink, parseCodeReviewId } from "./CodeReviewBlocker";
import {
  RESOLUTION_REASONS,
  STATUS_LABELS,
  humanTransitions,
  isTerminal,
} from "./investigationUtils";
import { saveErrorText } from "./versionConflict";

/**
 * Which of the selected cases an action can touch, and why the rest are left alone. Mirrors the
 * backend rules so the modal can say up front what will happen instead of reporting 403s after.
 */
export interface BulkPlan {
  eligible: Investigation[];
  /** Cases the action skips, grouped by the one-line reason shown in the modal. */
  skipped: Array<{ reason: string; investigations: Investigation[] }>;
}

const isMine = (inv: Investigation, currentUser: string | null) =>
  !!currentUser && inv.owner === currentUser;

function plan(
  selected: Investigation[],
  rules: Array<{ reason: string; skip: (inv: Investigation) => boolean }>
): BulkPlan {
  const skipped: BulkPlan["skipped"] = [];
  const eligible: Investigation[] = [];
  selected.forEach((inv) => {
    const rule = rules.find((r) => r.skip(inv));
    if (!rule) {
      eligible.push(inv);
      return;
    }
    const bucket = skipped.find((s) => s.reason === rule.reason);
    if (bucket) bucket.investigations.push(inv);
    else skipped.push({ reason: rule.reason, investigations: [inv] });
  });
  return { eligible, skipped };
}

/** Anyone may reassign, except resolved cases and ones the new owner already holds. */
export function planReassign(
  selected: Investigation[],
  newOwner: string
): BulkPlan {
  return plan(selected, [
    {
      reason: "closed — reopen first",
      skip: (inv) => isTerminal(inv.status),
    },
    {
      reason: `already owned by ${newOwner}`,
      skip: (inv) => !!newOwner && inv.owner === newOwner,
    },
  ]);
}

/** Only the owner may release; resolved cases have no ownership to release. */
export function planRelease(
  selected: Investigation[],
  currentUser: string | null
): BulkPlan {
  return plan(selected, [
    {
      reason: "closed — reopen first",
      skip: (inv) => isTerminal(inv.status),
    },
    {
      reason: "not yours — only the owner can release",
      skip: (inv) => !isMine(inv, currentUser),
    },
  ]);
}

/** Only the owner may change status, and only along a transition the case allows. */
export function planStatus(
  selected: Investigation[],
  currentUser: string | null,
  target: string
): BulkPlan {
  return plan(selected, [
    {
      reason: "not yours — only the owner can change status",
      skip: (inv) => !isMine(inv, currentUser),
    },
    {
      reason: `already ${STATUS_LABELS[target] ?? target}`,
      skip: (inv) => inv.status === target,
    },
    {
      reason: `cannot move to ${
        STATUS_LABELS[target] ?? target
      } from its current status`,
      skip: (inv) =>
        !humanTransitions(inv.status, inv.blocker?.blockedFrom).includes(
          target
        ),
    },
  ]);
}

/** The two ways to close a case in bulk. */
export type CloseTarget = "RESOLVED" | "WITHDRAWN";

/**
 * Only the owner may close — or anyone, when the case has no owner — a closed case is left
 * alone, and the case must allow the move
 * (e.g. a case in Monitoring redrive can be resolved but not cancelled; mid-redrive neither).
 */
export function planClose(
  selected: Investigation[],
  currentUser: string | null,
  target: CloseTarget
): BulkPlan {
  const verb = target === "RESOLVED" ? "resolved" : "cancelled";
  return plan(selected, [
    {
      reason: "not yours — only the owner can close",
      skip: (inv) => !!inv.owner && !isMine(inv, currentUser),
    },
    { reason: "already closed", skip: (inv) => isTerminal(inv.status) },
    {
      reason: `cannot be ${verb} from its current status`,
      skip: (inv) =>
        !humanTransitions(inv.status, inv.blocker?.blockedFrom).includes(
          target
        ),
    },
  ]);
}

/**
 * The per-case update for a bulk close. The shared summary becomes each case's conclusion
 * unless `keepExisting` is set and the case already has one.
 */
export function closeRequest(
  inv: Investigation,
  target: CloseTarget,
  summary: string,
  resolutionReason: string,
  keepExisting: boolean
): Omit<InvestigationUpdateRequest, "expectedVersion"> {
  const request: Omit<InvestigationUpdateRequest, "expectedVersion"> = {
    status: target,
  };
  if (target === "RESOLVED")
    request.resolutionReason =
      resolutionReason as InvestigationUpdateRequest["resolutionReason"];
  if (!(keepExisting && inv.conclusion?.summary?.trim()))
    request.conclusion = { ...(inv.conclusion || {}), summary };
  return request;
}

/**
 * Statuses offered in the bulk "Change status" menu: every transition some selected case of
 * the viewer's allows. Resolving is deliberately absent — it needs a conclusion per case.
 */
export function bulkStatusTargets(
  selected: Investigation[],
  currentUser: string | null
): string[] {
  const targets = new Set<string>();
  selected
    .filter((inv) => isMine(inv, currentUser))
    .forEach((inv) =>
      humanTransitions(inv.status, inv.blocker?.blockedFrom)
        .filter((t) => !isTerminal(t))
        .forEach((t) => targets.add(t))
    );
  return Object.keys(STATUS_LABELS).filter((s) => targets.has(s));
}

const count = (n: number) => `${n} investigation${n === 1 ? "" : "s"}`;

/** "3 skipped: 2 not yours — only the owner can release; 1 resolved — reopen first" */
const SkippedNote: React.FC<{ plan: BulkPlan }> = ({ plan: p }) => {
  const total = p.skipped.reduce((n, s) => n + s.investigations.length, 0);
  if (!total) return null;
  return (
    <Alert type="info" data-testid="bulk-skipped">
      {count(total)} will be skipped:{" "}
      {p.skipped
        .map((s) => `${s.investigations.length} ${s.reason}`)
        .join("; ")}
      .
    </Alert>
  );
};

/** Footer shared by the three modals. */
const ModalFooter: React.FC<{
  confirmLabel: string;
  disabled: boolean;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ confirmLabel, disabled, loading, onCancel, onConfirm }) => (
  <Box float="right">
    <SpaceBetween direction="horizontal" size="xs">
      <Button variant="link" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={onConfirm}
        disabled={disabled}
        loading={loading}
      >
        {confirmLabel}
      </Button>
    </SpaceBetween>
  </Box>
);

interface InvestigationBulkActionsProps {
  stage: Stage;
  /** The selected rows, in table order. */
  selected: Investigation[];
  currentUser: string | null;
  toasts: ToastsApi;
  /** Called after any action ran (successfully or not) so the page can refetch and clear the selection. */
  onDone: () => unknown;
}

type BulkModal =
  | { kind: "reassign" }
  | { kind: "release" }
  | { kind: "merge" }
  | { kind: "close" }
  | { kind: "status"; target: string };

/**
 * Header "Actions" menu for a multi-selected work-queue table: reassign, release or change the
 * status of every selected case at once, each confirmed in a modal that says how many cases the
 * action will really touch. The rules are the per-case ones (anyone may reassign; only the
 * owner may release or change status; resolved cases are locked), applied case by case.
 */
export const InvestigationBulkActions: React.FC<
  InvestigationBulkActionsProps
> = ({ stage, selected, currentUser, toasts, onDone }) => {
  const bulk = useBulkInvestigationActions(stage);
  const [modal, setModal] = useState<BulkModal | null>(null);
  const [newOwner, setNewOwner] = useState("");
  const [blockerReason, setBlockerReason] = useState("");
  const [blockerLink, setBlockerLink] = useState("");
  const [blockerReview, setBlockerReview] = useState("");
  const [closeTarget, setCloseTarget] = useState<CloseTarget>("RESOLVED");
  const [resolutionReason, setResolutionReason] = useState(
    RESOLUTION_REASONS[0].value
  );
  const [closeSummary, setCloseSummary] = useState("");
  const [keepExisting, setKeepExisting] = useState(true);
  const directory = useAssigneeDirectory(stage, modal?.kind === "reassign");

  const mine = selected.filter((inv) => isMine(inv, currentUser));
  const statusTargets = bulkStatusTargets(selected, currentUser);

  const open = (next: BulkModal) => {
    setNewOwner("");
    setBlockerReason("");
    setBlockerLink("");
    setBlockerReview("");
    setCloseTarget("RESOLVED");
    setResolutionReason(RESOLUTION_REASONS[0].value);
    setCloseSummary("");
    setKeepExisting(true);
    setModal(next);
  };
  const close = () => setModal(null);

  const report = (verb: string, outcome: BulkOutcome, detail?: string) => {
    if (outcome.succeeded.length) {
      toasts.push({
        type: "success",
        content: `${verb} ${count(outcome.succeeded.length)}${
          detail ? ` ${detail}` : ""
        }.`,
      });
    }
    if (outcome.failed.length) {
      toasts.push({
        type: "error",
        header: `${count(outcome.failed.length)} could not be updated`,
        content: (
          <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
            {outcome.failed.map(({ investigation, error }) => (
              <li key={investigation.id}>
                “{investigation.title}”:{" "}
                {saveErrorText(error, "Reload and retry.")}
              </li>
            ))}
          </ul>
        ),
      });
    }
  };

  const finish = async (
    verb: string,
    action: Promise<BulkOutcome>,
    detail?: string
  ) => {
    try {
      report(verb, await action, detail);
    } catch (err) {
      toasts.showError(err instanceof Error ? err.message : String(err));
    }
    close();
    await onDone();
  };

  const mergeable = selected.filter((inv) => !mergeBlocker(inv));
  const closable =
    planClose(selected, currentUser, "RESOLVED").eligible.length +
    planClose(selected, currentUser, "WITHDRAWN").eligible.length;
  const items: ButtonDropdownProps.ItemOrGroup[] = [
    { id: "reassign", text: "Reassign…" },
    {
      id: "release",
      text: "Release ownership",
      disabled: !mine.length,
      disabledReason: "None of the selected investigations are yours.",
    },
    {
      id: "merge",
      text: "Merge…",
      disabled: mergeable.length < 2,
      disabledReason:
        selected.length < 2
          ? "Select at least two investigations to merge."
          : "Fewer than two of the selected investigations can be merged (resolved, mid-redrive or awaiting an MCM).",
    },
    {
      id: "close",
      text: "Close…",
      disabled: !closable,
      disabledReason:
        "Only the owner can close (or anyone, if it has no owner); none of the selected investigations can be closed.",
    },
    {
      id: "status",
      text: "Change status",
      disabled: !statusTargets.length,
      disabledReason:
        "Only the owner can change status; none of your selected investigations can move.",
      items: statusTargets.map((s) => ({
        id: `status:${s}`,
        text: STATUS_LABELS[s],
      })),
    },
  ];

  const owner = normaliseAlias(newOwner);
  const reassignPlan = planReassign(selected, owner);
  const releasePlan = planRelease(selected, currentUser);
  const closePlan = planClose(selected, currentUser, closeTarget);
  const summary = closeSummary.trim();
  const keptCount = keepExisting
    ? closePlan.eligible.filter((inv) => inv.conclusion?.summary?.trim()).length
    : 0;
  const statusPlan =
    modal?.kind === "status"
      ? planStatus(selected, currentUser, modal.target)
      : null;
  // A CR in the link is waited on when every case can follow CRs. Bulk only unblocks; a queued
  // redrive is chosen per case.
  const bulkTracksCodeReviews =
    !!statusPlan?.eligible.length &&
    statusPlan.eligible.every((inv) => inv.codeReviewTracking);
  const bulkCodeReviewId = bulkTracksCodeReviews
    ? parseCodeReviewId(blockerLink)
    : null;

  return (
    <>
      <ButtonDropdown
        data-testid="bulk-actions"
        ariaLabel="Actions on selected investigations"
        expandableGroups
        expandToViewport
        disabled={!selected.length}
        loading={bulk.isRunning}
        loadingText={
          bulk.progress
            ? `Updating ${bulk.progress.done}/${bulk.progress.total}…`
            : undefined
        }
        items={items}
        onItemClick={({ detail }) => {
          if (detail.id === "reassign") open({ kind: "reassign" });
          else if (detail.id === "release") open({ kind: "release" });
          else if (detail.id === "merge") open({ kind: "merge" });
          else if (detail.id === "close") open({ kind: "close" });
          else if (detail.id.startsWith("status:"))
            open({ kind: "status", target: detail.id.slice("status:".length) });
        }}
      >
        Actions{selected.length ? ` (${selected.length})` : ""}
      </ButtonDropdown>

      {modal?.kind === "merge" && (
        <InvestigationMergeDialog
          stage={stage}
          candidates={mergeable}
          onDismiss={close}
          onMerged={(survivorId, mergedIds, pending) => {
            const survivor = selected.find((inv) => inv.id === survivorId);
            toasts.push({
              type: "success",
              content: `Merged ${count(mergedIds.length)} into “${
                survivor?.title ?? survivorId
              }”${
                pending ? " — items are being moved in the background" : ""
              }.`,
            });
            close();
            Promise.resolve(onDone()).catch(console.error);
          }}
        />
      )}

      {modal?.kind === "reassign" && (
        <Modal
          visible
          onDismiss={close}
          header={`Reassign ${count(selected.length)}`}
          footer={
            <ModalFooter
              confirmLabel={
                reassignPlan.eligible.length && owner
                  ? `Reassign ${count(reassignPlan.eligible.length)}`
                  : "Reassign"
              }
              disabled={
                !owner ||
                !directory.isKnown(owner) ||
                directory.isLoading ||
                !reassignPlan.eligible.length
              }
              loading={bulk.isRunning}
              onCancel={close}
              onConfirm={() => {
                finish(
                  "Reassigned",
                  bulk.transfer(reassignPlan.eligible, owner),
                  `to ${owner}`
                ).catch(console.error);
              }}
            />
          }
        >
          <SpaceBetween size="s">
            <TextContent>
              <p style={{ margin: 0 }}>
                Each previous owner is notified and kept as a contributor; every
                change is audited.
              </p>
            </TextContent>
            {directory.unavailable && (
              <Alert type="warning">
                User list could not be loaded; the alias is checked on submit.
              </Alert>
            )}
            <FormField
              label="New owner alias"
              description="Only people with access to Lusca Search can own an investigation."
              errorText={
                owner && !directory.isLoading && !directory.isKnown(owner)
                  ? `"${owner}" is not a Lusca Search user.`
                  : undefined
              }
            >
              <AssigneeAutosuggest
                directory={directory}
                value={newOwner}
                onChange={setNewOwner}
                pinned={
                  currentUser
                    ? [{ alias: currentUser, description: "you" }]
                    : []
                }
              />
            </FormField>
            {owner && <SkippedNote plan={reassignPlan} />}
          </SpaceBetween>
        </Modal>
      )}

      {modal?.kind === "release" && (
        <Modal
          visible
          onDismiss={close}
          header={`Release ownership of ${count(releasePlan.eligible.length)}?`}
          footer={
            <ModalFooter
              confirmLabel="Release ownership"
              disabled={!releasePlan.eligible.length}
              loading={bulk.isRunning}
              onCancel={close}
              onConfirm={() => {
                finish("Released", bulk.release(releasePlan.eligible)).catch(
                  console.error
                );
              }}
            />
          }
        >
          <SpaceBetween size="s">
            <TextContent>
              <p style={{ margin: 0 }}>
                They are left <strong>without an owner</strong> and move to the
                &ldquo;Needs an owner&rdquo; list until someone takes them. You
                stay on each case as a contributor.
              </p>
              <p style={{ margin: 0 }}>
                If you know who should pick them up, use <em>Reassign…</em>{" "}
                instead.
              </p>
            </TextContent>
            <SkippedNote plan={releasePlan} />
          </SpaceBetween>
        </Modal>
      )}

      {modal?.kind === "close" && (
        <Modal
          visible
          onDismiss={close}
          header={`Close ${count(closePlan.eligible.length)}`}
          footer={
            <ModalFooter
              confirmLabel={
                closeTarget === "RESOLVED"
                  ? `Resolve ${count(closePlan.eligible.length)}`
                  : `Cancel ${count(closePlan.eligible.length)}`
              }
              disabled={!closePlan.eligible.length || !summary}
              loading={bulk.isRunning}
              onCancel={close}
              onConfirm={() => {
                finish(
                  closeTarget === "RESOLVED" ? "Resolved" : "Cancelled",
                  bulk.updateEach(closePlan.eligible, (inv) =>
                    closeRequest(
                      inv,
                      closeTarget,
                      summary,
                      resolutionReason,
                      keepExisting
                    )
                  )
                ).catch(console.error);
              }}
            />
          }
        >
          <SpaceBetween size="s">
            <FormField label="How are they closing?">
              <RadioGroup
                value={closeTarget}
                onChange={({ detail }) =>
                  setCloseTarget(detail.value as CloseTarget)
                }
                items={[
                  {
                    value: "RESOLVED",
                    label: "Resolve",
                    description: "The work is done; counted as resolved.",
                  },
                  {
                    value: "WITHDRAWN",
                    label: "Cancel",
                    description:
                      "Let them go; items return to the uninvestigated pool.",
                  },
                ]}
              />
            </FormField>
            {closeTarget === "RESOLVED" && (
              <FormField label="Resolution reason">
                <Select
                  selectedOption={
                    RESOLUTION_REASONS.find(
                      (r) => r.value === resolutionReason
                    ) || RESOLUTION_REASONS[0]
                  }
                  onChange={({ detail }) =>
                    setResolutionReason(
                      detail.selectedOption.value || RESOLUTION_REASONS[0].value
                    )
                  }
                  options={RESOLUTION_REASONS}
                />
              </FormField>
            )}
            <FormField
              label="Conclusion summary"
              description={
                closeTarget === "RESOLVED"
                  ? "What was found and what was done about it."
                  : "Why these are being cancelled — shown to whoever picks the items up next."
              }
              errorText={!summary ? "Required" : undefined}
            >
              <Textarea
                value={closeSummary}
                onChange={({ detail }) => setCloseSummary(detail.value)}
                rows={3}
              />
            </FormField>
            <Checkbox
              checked={keepExisting}
              onChange={({ detail }) => setKeepExisting(detail.checked)}
              description={
                keptCount
                  ? `${count(keptCount)} already ${
                      keptCount === 1 ? "has" : "have"
                    } a conclusion and will keep it.`
                  : undefined
              }
            >
              Keep conclusions already written
            </Checkbox>
            <SkippedNote plan={closePlan} />
          </SpaceBetween>
        </Modal>
      )}

      {modal?.kind === "status" && statusPlan && (
        <Modal
          visible
          onDismiss={close}
          header={`Move ${count(statusPlan.eligible.length)} to ${
            STATUS_LABELS[modal.target] ?? modal.target
          }`}
          footer={
            <ModalFooter
              confirmLabel="Confirm"
              disabled={
                !statusPlan.eligible.length ||
                (modal.target === "BLOCKED" && !blockerReason.trim())
              }
              loading={bulk.isRunning}
              onCancel={close}
              onConfirm={() => {
                const request: Omit<
                  InvestigationUpdateRequest,
                  "expectedVersion"
                > = {
                  status: modal.target as InvestigationUpdateRequest["status"],
                };
                if (modal.target === "BLOCKED") {
                  request.blocker = {
                    reason: blockerReason.trim(),
                    link: normalizeBlockerLink(blockerLink),
                    nextReviewDate: blockerReview || undefined,
                    codeReview: bulkCodeReviewId
                      ? { id: bulkCodeReviewId, afterDeploy: "NONE" }
                      : undefined,
                  };
                }
                finish(
                  "Moved",
                  bulk.updateStatus(statusPlan.eligible, request),
                  `to ${STATUS_LABELS[modal.target] ?? modal.target}`
                ).catch(console.error);
              }}
            />
          }
        >
          <SpaceBetween size="s">
            {modal.target === "BLOCKED" ? (
              <>
                <BlockerReasonField
                  value={blockerReason}
                  onChange={setBlockerReason}
                  description="Applied to every selected investigation."
                />
                <FormField
                  label="Ticket or external link (optional)"
                  description={
                    bulkTracksCodeReviews
                      ? "Link a CR (CR-123 or its code.amazon.com link) and Lusca follows it to each case's stage."
                      : undefined
                  }
                >
                  <Input
                    value={blockerLink}
                    onChange={({ detail }) => setBlockerLink(detail.value)}
                    placeholder={
                      bulkTracksCodeReviews
                        ? "https://issues.amazon.com/… or CR-123456789"
                        : "https://issues.amazon.com/…"
                    }
                  />
                </FormField>
                {bulkCodeReviewId && (
                  <Box
                    fontSize="body-s"
                    color="text-body-secondary"
                    data-testid="bulk-code-review-follow-up"
                  >
                    When {bulkCodeReviewId} reaches a case&apos;s stage, that
                    case is unblocked. To queue a redrive instead, block cases
                    one at a time.
                  </Box>
                )}
                <FormField label="Next review date (optional)">
                  <DatePicker
                    value={blockerReview}
                    onChange={({ detail }) => setBlockerReview(detail.value)}
                    placeholder="YYYY/MM/DD"
                  />
                </FormField>
              </>
            ) : (
              <TextContent>
                <p style={{ margin: 0 }}>
                  Move the selected investigations to{" "}
                  {STATUS_LABELS[modal.target] ?? modal.target}? Each change is
                  recorded in that investigation&rsquo;s audit trail.
                </p>
              </TextContent>
            )}
            <SkippedNote plan={statusPlan} />
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};

export default InvestigationBulkActions;
